/**
 * Shared room-service modals: add, edit, and delete-confirm.
 *
 * The room page and the space-detail room cards manage the same RoomService
 * records, but each used to carry its own modal copy — and the copies diverged
 * once already (fixed in PR #404). One implementation, imported by both, so
 * they can't drift again.
 *
 * Each modal owns its tRPC mutation and invalidates `rooms.get` for the
 * affected room. Page-specific refreshes (e.g. `spaces.get` on space-detail)
 * belong in the caller's `onSuccess`.
 */

import {
	extractNdiUrlAddress,
	parseNdiUrlAddress,
	ScreenFormat,
	SourceType,
	unknownNdiQueryKeys,
} from '@polygraf/shared'
import { Edit2, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, Button, Modal, Select, Spinner, TextField } from '@/components/ui'
import { trpc } from '@/trpc/client'

/**
 * Service shape the edit/delete modals accept. Deliberately loose on
 * `type`/`format` (plain string unions from `spaces.get`, shared enums from
 * `rooms.get`) and optional on `endpoint`, which `spaces.get` omits.
 */
export interface EditableService {
	id: string
	type: SourceType | string
	name: string
	endpoint?: string | null
	format?: ScreenFormat | string | null
}

/**
 * Placeholder URL per presentation-image protocol. A SCREEN service with no
 * static format is a live browser share over LiveKit — the default. NDI uses
 * the `ndi://MACHINE/Channel` form (the machine + channel of the NDI source
 * name `MACHINE (Channel)`), transcoded to HLS by the media-relay.
 *
 * The http(s) examples are deliberately MUNI-shaped rather than the generic
 * `192.168.x` / `example.edu` ones they replaced. Those were unsaveable: the
 * server runs an untrusted allowlist over every http(s) endpoint
 * (`rooms.ts:assertEndpointAllowed`) which admits `*.muni.cz` and the campus
 * range `147.251.0.0/16` and rejects RFC1918 in production, apart from what a
 * system admin saves inside a range the operator enabled — so copying the
 * placeholder shape produced "Source URL is not allowed by the server security
 * policy" and no hint as to what WOULD work. `147.251.36.3:1881` is the real
 * classroom camera from the 2026-07-24 MU session. RTSP/NDI are exempt (they
 * go to MediaMTX, not our fetcher), so their examples stay illustrative.
 */
const SCREEN_ENDPOINT_PLACEHOLDER: Record<string, string> = {
	[ScreenFormat.JPG]: 'http://147.251.36.3:1881/vgabroadcaster.jpg',
	[ScreenFormat.MJPG]: 'http://147.251.36.3:1881/stream.mjpg',
	[ScreenFormat.RTSP]: 'rtsp://kamera.muni.cz/stream',
	[ScreenFormat.HLS]: 'https://stream.muni.cz/live/index.m3u8',
	[ScreenFormat.MP4]: 'https://stream.muni.cz/prednaska.mp4',
	[ScreenFormat.NDI]: 'ndi://STUDIO-A/Camera 1?url=10.56.46.10:5961',
}

/**
 * Validation error for a static SCREEN endpoint, or undefined when it's fine.
 *
 * NDI endpoints may carry an optional `?url=HOST:PORT` direct-connect address
 * and no other query key. Lives here (not inside the field) so BOTH the field's
 * error state and the submit button's `disabled` key off the same result —
 * otherwise the field goes red while the button stays live and the server
 * rejects the same input with a different message. Mirrors the server's checks
 * in `rooms.assertEndpointSchemeForFormat` using the same shared helpers.
 */
function screenEndpointError(
	format: ScreenFormat | null,
	endpoint: string,
	t: (key: string, options?: Record<string, unknown>) => string,
): string | undefined {
	if (format === null || !endpoint) return undefined
	let scheme: string
	try {
		scheme = new URL(endpoint).protocol
	} catch {
		return t('rooms.urlInvalid')
	}
	if (format === ScreenFormat.RTSP) {
		return scheme === 'rtsp:' || scheme === 'rtsps:' ? undefined : t('rooms.rtspSchemeRequired')
	}
	if (format === ScreenFormat.NDI) {
		if (scheme !== 'ndi:') return t('rooms.ndiSchemeRequired')
		const unknownKeys = unknownNdiQueryKeys(endpoint)
		if (unknownKeys.length > 0) {
			return t('rooms.ndiUnknownParam', { params: unknownKeys.join(', ') })
		}
		const rawUrlAddr = extractNdiUrlAddress(endpoint)
		if (rawUrlAddr !== null && parseNdiUrlAddress(rawUrlAddr) === null) {
			return t('rooms.ndiAddressInvalid')
		}
		return undefined
	}
	if (scheme !== 'http:' && scheme !== 'https:') {
		return t('rooms.httpSchemeRequired')
	}
	return undefined
}

/**
 * The server's write-path validation speaks English (`rooms.ts`
 * `assertEndpointSchemeForFormat` / the SSRF allowlist). The scheme half is
 * mirrored above so users normally never see it — but the allowlist verdict
 * only the server knows, and defence-in-depth errors can still surface. Map
 * the known messages onto the same localized strings; anything unrecognized
 * falls through verbatim.
 */
const SERVER_ERROR_PATTERNS: ReadonlyArray<readonly [RegExp, string]> = [
	[/not allowed by the server security policy/i, 'rooms.urlNotAllowed'],
	[/format of a private-network source/i, 'rooms.formatPrivateAdminOnly'],
	[/can only be set by a system administrator/i, 'rooms.urlPrivateAdminOnly'],
	[/not enabled on this server/i, 'rooms.urlPrivateNotEnabled'],
	[/not a valid URL/i, 'rooms.urlInvalid'],
	[/rtsp:\/\/ or rtsps:\/\//i, 'rooms.rtspSchemeRequired'],
	[/require an ndi:\/\//i, 'rooms.ndiSchemeRequired'],
	[/http:\/\/ or https:\/\//i, 'rooms.httpSchemeRequired'],
]

function localizeServiceError(
	message: string,
	t: (key: string, options?: Record<string, unknown>) => string,
): string {
	for (const [pattern, key] of SERVER_ERROR_PATTERNS) {
		if (pattern.test(message)) return t(key)
	}
	return message
}

function ScreenSourceFields({
	format,
	setFormat,
	endpoint,
	setEndpoint,
	endpointError,
}: {
	format: ScreenFormat | null
	setFormat: (format: ScreenFormat | null) => void
	endpoint: string
	setEndpoint: (endpoint: string) => void
	endpointError?: string
}) {
	const { t } = useTranslation()
	return (
		<>
			<Select
				label={t('rooms.screenSourceKind')}
				hint={t('rooms.screenSourceKindHint')}
				value={format ?? 'LIVE'}
				onChange={(e) =>
					setFormat(e.target.value === 'LIVE' ? null : (e.target.value as ScreenFormat))
				}
			>
				{/* The two service types collapsed into one "Prezentační obraz"
				    with a protocol dropdown (MU 2026-07-24 #6): browser share +
				    every image protocol. JPEG/MJPEG are served on-demand by the
				    proxy like the others — no background capture. */}
				<option value="LIVE">{t('rooms.screenLiveShare')}</option>
				<option value={ScreenFormat.JPG}>{t('rooms.jpegSnapshot')}</option>
				<option value={ScreenFormat.MJPG}>{t('rooms.mjpegStream')}</option>
				<option value={ScreenFormat.RTSP}>{t('rooms.rtspCamera')}</option>
				<option value={ScreenFormat.NDI}>{t('rooms.ndiSource')}</option>
				<option value={ScreenFormat.HLS}>{t('rooms.hlsStream')}</option>
				<option value={ScreenFormat.MP4}>{t('rooms.mp4Video')}</option>
			</Select>

			{format && (
				<TextField
					// NDI endpoints (ndi://MACHINE/Channel) can contain spaces that
					// the browser's native url validation rejects, so use a text
					// input for NDI and url validation for the true-URL formats.
					type={format === ScreenFormat.NDI ? 'text' : 'url'}
					label={t('rooms.sourceUrl')}
					required
					placeholder={SCREEN_ENDPOINT_PLACEHOLDER[format] ?? ''}
					hint={format === ScreenFormat.NDI ? t('rooms.ndiAddressHint') : undefined}
					error={endpointError}
					value={endpoint}
					onChange={(e) => setEndpoint(e.target.value)}
				/>
			)}

			{/* NDI SDK licence requires the trademark notice where NDI is used. */}
			{format === ScreenFormat.NDI && (
				<p className="text-xs text-base-content/60">{t('rooms.ndiTrademark')}</p>
			)}
		</>
	)
}

export function AddServiceModal({
	roomId,
	onClose,
	onSuccess,
}: {
	roomId: string
	onClose: () => void
	onSuccess: () => void
}) {
	const { t } = useTranslation()
	const utils = trpc.useUtils()
	const [name, setName] = useState('')
	const [type, setType] = useState<SourceType>(SourceType.TRANSCRIPT)
	const [endpoint, setEndpoint] = useState('')
	// Presentation-image protocol: null = live browser share (LiveKit); a
	// ScreenFormat = a static/relayed source (JPEG/MJPEG/RTSP/NDI/HLS/MP4). All
	// image protocols are the single SCREEN type now — SCREENCAST is retired for
	// new services (MU 2026-07-24 #6).
	const [screenFormat, setScreenFormat] = useState<ScreenFormat | null>(null)

	const addMutation = trpc.rooms.addService.useMutation({
		onSuccess: () => {
			utils.rooms.get.invalidate({ id: roomId })
			setName('')
			setEndpoint('')
			onSuccess()
		},
	})

	const isScreen = type === SourceType.SCREEN
	// A SCREEN service is static (needs an endpoint) only when a protocol is
	// picked; otherwise it's a LiveKit live share with no endpoint.
	const isStaticScreen = isScreen && screenFormat !== null
	const endpointError = isStaticScreen ? screenEndpointError(screenFormat, endpoint, t) : undefined

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		if (!name.trim() || endpointError) return
		// Static image protocol → endpoint + format. Live browser share (LiveKit)
		// and TRANSCRIPT carry neither.
		addMutation.mutate({
			roomId,
			name: name.trim(),
			type,
			endpoint: isStaticScreen ? endpoint : undefined,
			format: isStaticScreen ? (screenFormat ?? undefined) : undefined,
		})
	}

	return (
		<Modal
			open
			onClose={onClose}
			title={t('rooms.addService')}
			closeLabel={t('common.close')}
			footer={
				<>
					<Button variant="ghost" onClick={onClose}>
						{t('common.cancel')}
					</Button>
					<Button
						type="submit"
						form="add-service-form"
						leadingIcon={<Plus size={14} />}
						loading={addMutation.isPending}
						disabled={!name.trim() || (isStaticScreen && !endpoint) || !!endpointError}
					>
						{t('common.create')}
					</Button>
				</>
			}
		>
			<form id="add-service-form" onSubmit={handleSubmit} className="space-y-4">
				<TextField
					label={t('rooms.serviceName')}
					required
					placeholder={t('rooms.serviceName')}
					value={name}
					onChange={(e) => setName(e.target.value)}
					autoFocus
				/>

				<Select
					label={t('spaceDetail.modal.serviceType')}
					value={type}
					onChange={(e) => setType(e.target.value as SourceType)}
				>
					<option value={SourceType.TRANSCRIPT}>{t('rooms.transcript')}</option>
					<option value={SourceType.SCREEN}>{t('rooms.screen')}</option>
				</Select>

				{isScreen && (
					<ScreenSourceFields
						format={screenFormat}
						setFormat={setScreenFormat}
						endpoint={endpoint}
						setEndpoint={setEndpoint}
						endpointError={endpointError}
					/>
				)}

				{addMutation.error && (
					<Alert tone="error">{localizeServiceError(addMutation.error.message, t)}</Alert>
				)}
			</form>
		</Modal>
	)
}

export function EditServiceModal({
	service,
	roomId,
	onClose,
	onSuccess,
}: {
	service: EditableService
	roomId: string
	onClose: () => void
	onSuccess: () => void
}) {
	const { t } = useTranslation()
	// `spaces.get` strips service endpoints, so a SCREENCAST / static-SCREEN
	// service arriving from the space-detail page has no `endpoint` field.
	// Hydrate it from rooms.get before mounting the form — otherwise editing
	// would force retyping (or silently rewriting) the source URL.
	const needsEndpoint =
		(service.type === SourceType.SCREENCAST || service.type === SourceType.SCREEN) &&
		service.endpoint === undefined
	const roomQuery = trpc.rooms.get.useQuery({ id: roomId }, { enabled: needsEndpoint })

	if (needsEndpoint && !roomQuery.data) {
		return (
			<Modal open onClose={onClose} title={t('rooms.editService')} closeLabel={t('common.close')}>
				{roomQuery.error ? (
					<Alert tone="error">{roomQuery.error.message}</Alert>
				) : (
					<div className="flex justify-center py-6">
						<Spinner />
					</div>
				)}
			</Modal>
		)
	}

	const resolved = needsEndpoint
		? {
				...service,
				endpoint: roomQuery.data?.services.find((s) => s.id === service.id)?.endpoint ?? null,
			}
		: service

	return (
		<EditServiceForm service={resolved} roomId={roomId} onClose={onClose} onSuccess={onSuccess} />
	)
}

function EditServiceForm({
	service,
	roomId,
	onClose,
	onSuccess,
}: {
	service: EditableService
	roomId: string
	onClose: () => void
	onSuccess: () => void
}) {
	const { t } = useTranslation()
	const utils = trpc.useUtils()
	// Legacy SCREENCAST services still exist (SCREENCAST is retired only for NEW
	// ones) and edit across the same protocol set as a SCREEN service — the
	// server migrates such a row to SCREEN when a protocol is chosen. Offering a
	// shorter list here was what MU saw as "sedm typů zdrojů při vytváření, dva
	// při editaci" (2026-08-04), and it left their pre-#524 cameras unable to
	// move to RTSP/NDI. Both types are "Prezentační obraz" to the user (#6).
	const isScreencast = service.type === SourceType.SCREENCAST
	const isImage = isScreencast || service.type === SourceType.SCREEN
	const [name, setName] = useState(service.name)
	const [endpoint, setEndpoint] = useState(service.endpoint ?? '')
	// null = live browser share (LiveKit); a ScreenFormat = static source.
	// SCREENCAST always carries a JPG/MJPG format, so it's never a live share.
	const [screenFormat, setScreenFormat] = useState<ScreenFormat | null>(() =>
		isScreencast
			? ((service.format as ScreenFormat | null) ?? ScreenFormat.JPG)
			: ((service.format as ScreenFormat | null) ?? null),
	)

	const updateMutation = trpc.rooms.updateService.useMutation({
		onSuccess: () => {
			utils.rooms.get.invalidate({ id: roomId })
			onSuccess()
		},
	})

	const isStaticScreen = isImage && screenFormat !== null
	const endpointError = isStaticScreen ? screenEndpointError(screenFormat, endpoint, t) : undefined

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		if (!name.trim() || endpointError) return
		updateMutation.mutate({
			serviceId: service.id,
			name: name.trim(),
			// Static image protocol → endpoint + format. Live browser share
			// (SCREEN only, format null) clears both. TRANSCRIPT: leave untouched.
			endpoint: isImage ? (isStaticScreen ? endpoint : null) : undefined,
			format: isImage ? screenFormat : undefined,
		})
	}

	return (
		<Modal
			open
			onClose={onClose}
			title={t('rooms.editService')}
			closeLabel={t('common.close')}
			footer={
				<>
					<Button variant="ghost" onClick={onClose}>
						{t('common.cancel')}
					</Button>
					<Button
						type="submit"
						form="edit-service-form"
						leadingIcon={<Edit2 size={14} />}
						loading={updateMutation.isPending}
						disabled={!name.trim() || (isStaticScreen && !endpoint) || !!endpointError}
					>
						{t('common.save')}
					</Button>
				</>
			}
		>
			<form id="edit-service-form" onSubmit={handleSubmit} className="space-y-4">
				<TextField
					label={t('rooms.serviceName')}
					required
					placeholder={t('rooms.serviceName')}
					value={name}
					onChange={(e) => setName(e.target.value)}
					autoFocus
				/>

				{isImage && (
					<ScreenSourceFields
						format={screenFormat}
						setFormat={setScreenFormat}
						endpoint={endpoint}
						setEndpoint={setEndpoint}
						endpointError={endpointError}
					/>
				)}

				{updateMutation.error && (
					<Alert tone="error">{localizeServiceError(updateMutation.error.message, t)}</Alert>
				)}
			</form>
		</Modal>
	)
}

export function DeleteServiceModal({
	serviceId,
	roomId,
	open,
	onClose,
	onSuccess,
}: {
	serviceId: string
	roomId: string
	open: boolean
	onClose: () => void
	onSuccess: () => void
}) {
	const { t } = useTranslation()
	const utils = trpc.useUtils()
	const removeMutation = trpc.rooms.removeService.useMutation({
		onSuccess: () => {
			utils.rooms.get.invalidate({ id: roomId })
			onSuccess()
		},
	})

	const handleDelete = async () => {
		try {
			await removeMutation.mutateAsync({ serviceId })
		} catch {
			// Surfaced via removeMutation.error below.
		}
	}

	return (
		<Modal
			open={open}
			onClose={onClose}
			title={t('rooms.confirmDeleteServiceTitle')}
			closeLabel={t('common.close')}
			size="sm"
			persistent
			footer={
				<>
					<Button type="button" variant="ghost" onClick={onClose}>
						{t('common.cancel')}
					</Button>
					<Button
						type="button"
						variant="danger"
						loading={removeMutation.isPending}
						onClick={handleDelete}
					>
						{t('common.delete')}
					</Button>
				</>
			}
		>
			<p className="text-sm text-base-content/70">{t('rooms.confirmDeleteService')}</p>
			{removeMutation.error && (
				<Alert tone="error" className="mt-4">
					{removeMutation.error.message}
				</Alert>
			)}
		</Modal>
	)
}
